
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface SellerNotification {
  id: string;
  seller_id: string;
  property_id: string | null;
  buyer_id: string | null;
  viewed_at: string;
  read_status: boolean;
  created_at: string;
  type: string;
  metadata?: {
    task_id?: string;
    lead_id?: string;
    task_title?: string;
    deal_name?: string;
    due_date?: string;
    is_overdue?: boolean;
  } | null;
  property?: {
    address: string;
    city: string;
    state: string;
    price: number;
    image_url: string | null;
  } | null;
  buyer?: {
    full_name: string;
    email: string;
    phone_number: string;
    company_name: string | null;
    profile_image_url: string | null;
  } | null;
}

export const notificationsService = {
  // Get all notifications for a seller
  async getSellerNotifications(sellerId: string): Promise<SellerNotification[]> {
    try {
      const { data, error } = await supabase
        .from('seller_notifications')
        .select(`
          id,
          seller_id,
          property_id,
          buyer_id,
          viewed_at,
          read_status,
          created_at,
          type,
          metadata,
          properties:properties!left(address, city, state, price, image_url),
          buyer_profiles:buyer_profiles!left(full_name, email, phone_number, company_name, profile_image_url)
        `)
        .eq('seller_id', sellerId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching seller notifications', { error: error.message });
        return [];
      }

      // Transform the data to match our interface
      const transformedData = data?.map((notification: any) => ({
        ...notification,
        property: notification.properties,
        buyer: notification.buyer_profiles
      })) || [];

      return transformedData;
    } catch (error) {
      logger.error('Error fetching seller notifications', { error });
      return [];
    }
  },

  // Get unread notification count for a seller
  async getUnreadCount(sellerId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('seller_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('seller_id', sellerId)
        .eq('read_status', false);

      if (error) {
        logger.error('Error fetching unread count', { error: error.message });
        return 0;
      }

      return count || 0;
    } catch (error) {
      logger.error('Error fetching unread count', { error });
      return 0;
    }
  },

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('seller_notifications')
        .update({ read_status: true })
        .eq('id', notificationId);

      if (error) {
        logger.error('Error marking notification as read', { error: error.message });
      }
    } catch (error) {
      logger.error('Error marking notification as read', { error });
    }
  },

  // Mark all notifications as read for a seller
  async markAllAsRead(sellerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('seller_notifications')
        .update({ read_status: true })
        .eq('seller_id', sellerId)
        .eq('read_status', false);

      if (error) {
        logger.error('Error marking all notifications as read', { error: error.message });
      }
    } catch (error) {
      logger.error('Error marking all notifications as read', { error });
    }
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(sellerId: string, callback: (notification: any) => void) {
    const channel = supabase
      .channel('seller-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'seller_notifications',
          filter: `seller_id=eq.${sellerId}`
        },
        callback
      )
      .subscribe();

    return channel;
  }
};
